import type { BuildOptions, GuidedBuildDefinition, GuidedBuildPreview } from "./api.js";

export type GeneratedFieldKey = "name" | "clan" | "concept" | "appearance" | "story" | "notes";

export interface GenerationState {
  fingerprint: string;
  counters: Record<GeneratedFieldKey, number>;
  manual: Record<GeneratedFieldKey, boolean>;
}

export interface GeneratedCharacterPayload {
  characterName: string;
  nameKanji: string;
  nameMeaning: string;
  playerName: string;
  clan: string;
  clanDescription: string;
  characterConcept: string;
  background: string;
  appearance: string;
  notes: string;
  rumor: string;
  goal: string;
  bond: string;
  originId: string;
  arcanePowerIds: string[];
  guidedAnswers: Record<string, string>;
  generation: GenerationState;
  [key: string]: unknown;
}

export interface GeneratorContext {
  options: BuildOptions;
  definition: GuidedBuildDefinition;
  preview: GuidedBuildPreview;
  answers: Record<string, string>;
  originId: string;
}

interface NameEntry {
  romanized: string;
  kanji: string;
  meaning: string;
}

interface ClanEntry {
  name: string;
  image: string;
  custom: string;
}

const FIELD_KEYS: GeneratedFieldKey[] = ["name", "clan", "concept", "appearance", "story", "notes"];

export function emptyGenerationState(): GenerationState {
  return {
    fingerprint: "",
    counters: { name: 0, clan: 0, concept: 0, appearance: 0, story: 0, notes: 0 },
    manual: { name: false, clan: false, concept: false, appearance: false, story: false, notes: false },
  };
}

const GENERAL_NAMES: NameEntry[] = [
  { romanized: "Akira", kanji: "明", meaning: "brightness or clarity" },
  { romanized: "Haru", kanji: "春", meaning: "spring" },
  { romanized: "Kaede", kanji: "楓", meaning: "maple" },
  { romanized: "Makoto", kanji: "誠", meaning: "sincerity" },
  { romanized: "Nagi", kanji: "凪", meaning: "a calm sea or lull in the wind" },
  { romanized: "Ren", kanji: "蓮", meaning: "lotus" },
  { romanized: "Rin", kanji: "凛", meaning: "dignified and resolute" },
  { romanized: "Sora", kanji: "空", meaning: "sky" },
];

const NAMES_BY_SPECIES: Record<string, NameEntry[]> = {
  "species.kappa": [
    { romanized: "Midori", kanji: "翠", meaning: "jade green" },
    { romanized: "Suiren", kanji: "睡蓮", meaning: "water lily" },
    { romanized: "Nagisa", kanji: "渚", meaning: "shore" },
    { romanized: "Minato", kanji: "湊", meaning: "harbor" },
  ],
  "species.kitsune": [
    { romanized: "Akari", kanji: "灯", meaning: "lamplight" },
    { romanized: "Kohaku", kanji: "琥珀", meaning: "amber" },
    { romanized: "Hikari", kanji: "光", meaning: "light" },
    { romanized: "Tsukiko", kanji: "月子", meaning: "moon child" },
  ],
  "species.oni": [
    { romanized: "Isamu", kanji: "勇", meaning: "courage" },
    { romanized: "Daichi", kanji: "大地", meaning: "great earth" },
    { romanized: "Kurenai", kanji: "紅", meaning: "crimson" },
    { romanized: "Tora", kanji: "虎", meaning: "tiger" },
  ],
  "species.yamabushi_tengu": [
    { romanized: "Hayate", kanji: "颯", meaning: "swift wind" },
    { romanized: "Tsubasa", kanji: "翼", meaning: "wing" },
    { romanized: "Arashi", kanji: "嵐", meaning: "storm" },
    { romanized: "Shun", kanji: "俊", meaning: "talented or exceptional" },
  ],
  "species.bakeneko": [
    { romanized: "Yoru", kanji: "夜", meaning: "night" },
    { romanized: "Tama", kanji: "珠", meaning: "jewel" },
    { romanized: "Miko", kanji: "美子", meaning: "beautiful child" },
    { romanized: "Kage", kanji: "影", meaning: "shadow" },
  ],
  "species.inugami_jiyuu": [
    { romanized: "Shin", kanji: "信", meaning: "trust or faith" },
    { romanized: "Mamoru", kanji: "守", meaning: "to protect" },
    { romanized: "Tadao", kanji: "忠雄", meaning: "loyal hero" },
    { romanized: "Akio", kanji: "明夫", meaning: "bright man" },
  ],
  "species.jorogumo": [
    { romanized: "Kinu", kanji: "絹", meaning: "silk" },
    { romanized: "Ayame", kanji: "菖蒲", meaning: "iris" },
    { romanized: "Shiori", kanji: "栞", meaning: "bookmark or guide" },
    { romanized: "Renka", kanji: "蓮花", meaning: "lotus flower" },
  ],
  "species.karakasa_kozo": [
    { romanized: "Shigure", kanji: "時雨", meaning: "late-autumn rain" },
    { romanized: "Ame", kanji: "雨", meaning: "rain" },
    { romanized: "Yoi", kanji: "宵", meaning: "early evening" },
    { romanized: "Matoi", kanji: "纏", meaning: "to wrap or gather" },
  ],
  "species.medama": [
    { romanized: "Hitomi", kanji: "瞳", meaning: "pupil of the eye" },
    { romanized: "Satori", kanji: "悟", meaning: "understanding" },
    { romanized: "Rei", kanji: "玲", meaning: "the clear sound of jewels" },
    { romanized: "Sen", kanji: "千", meaning: "one thousand" },
  ],
  "species.mizuchi": [
    { romanized: "Aoi", kanji: "碧", meaning: "deep blue-green" },
    { romanized: "Ryū", kanji: "龍", meaning: "dragon" },
    { romanized: "Umi", kanji: "海", meaning: "sea" },
    { romanized: "Nagisa", kanji: "渚", meaning: "shore" },
  ],
  "species.noppera_bo": [
    { romanized: "Mumei", kanji: "無名", meaning: "nameless" },
    { romanized: "Shiro", kanji: "白", meaning: "white or blank" },
    { romanized: "Rei", kanji: "零", meaning: "zero" },
    { romanized: "Kage", kanji: "影", meaning: "shadow" },
  ],
  "species.raiju": [
    { romanized: "Hibiki", kanji: "響", meaning: "echo or resonance" },
    { romanized: "Rai", kanji: "雷", meaning: "thunder" },
    { romanized: "Raiden", kanji: "雷電", meaning: "thunder and lightning" },
    { romanized: "Shiden", kanji: "紫電", meaning: "violet lightning" },
  ],
  "species.rokurokubi": [
    { romanized: "Chiyo", kanji: "千代", meaning: "a thousand generations" },
    { romanized: "Saki", kanji: "咲", meaning: "to bloom" },
    { romanized: "Ayaka", kanji: "彩花", meaning: "colorful flower" },
    { romanized: "Nobu", kanji: "伸", meaning: "to stretch or extend" },
  ],
  "species.tanuki": [
    { romanized: "Kichi", kanji: "吉", meaning: "good fortune" },
    { romanized: "Fuku", kanji: "福", meaning: "luck or blessing" },
    { romanized: "Raku", kanji: "楽", meaning: "ease or enjoyment" },
    { romanized: "Mame", kanji: "豆", meaning: "bean" },
  ],
  "species.yuki_onna": [
    { romanized: "Yuki", kanji: "雪", meaning: "snow" },
    { romanized: "Fuyumi", kanji: "冬美", meaning: "winter beauty" },
    { romanized: "Mafuyu", kanji: "真冬", meaning: "midwinter" },
    { romanized: "Shirayuki", kanji: "白雪", meaning: "white snow" },
  ],
  "species.hanyo": GENERAL_NAMES,
};

const CLANS: ClanEntry[] = [
  { name: "Clan of the Rain-Stained Lantern", image: "a lantern that never goes out in bad weather", custom: "It shelters wanderers, couriers, and yōkai whose work begins after the last train." },
  { name: "Clan of the Crooked Torii", image: "an old gate standing at the wrong angle", custom: "It controls hidden passages between crowded streets, neglected shrines, and forgotten places." },
  { name: "Clan of the Seventh Bell", image: "a bell heard only after six ordinary chimes", custom: "Its members trade warnings, debts, and supernatural intelligence across modern Japan." },
  { name: "Clan of the Red Thread", image: "a thread tied around promises that should not be broken", custom: "It binds humans and yōkai through favors, relationships, and obligations that can last generations." },
  { name: "Clan of the Quiet Current", image: "water moving beneath concrete", custom: "It protects old routes, waterways, and secrets carried through the city without leaving a trace." },
  { name: "Clan of the Black Pine", image: "a pine tree surviving where nothing else should grow", custom: "It values endurance, territory, and the patient defense of places humans have nearly forgotten." },
  { name: "Clan of the Empty Mask", image: "a blank face kept among a hundred identities", custom: "It specializes in disguise, negotiation, information, and the careful management of human witnesses." },
  { name: "Clan of the Last Train", image: "the final carriage passing after the timetable ends", custom: "It moves people, messages, and dangerous objects through stations, roads, and liminal hours." },
  { name: "Clan of the White Cinders", image: "ashes that remain warm until dawn", custom: "It gathers survivors of violent legends and turns fear into protection, vengeance, or political leverage." },
  { name: "Clan of the Glass Moon", image: "moonlight reflected in towers and phone screens", custom: "It studies how old supernatural customs mutate inside media, nightlife, and the modern city." },
];

const SPECIES_APPEARANCE: Record<string, string[]> = {
  "species.kappa": ["webbed hands, a shell-like back, and the unmistakable water-filled dish of a kappa", "compact, amphibious features hidden beneath practical street clothes"],
  "species.kitsune": ["foxlike eyes, a carefully controlled smile, and hints of a tail that appear in reflections", "an elegant human guise that never entirely hides the alertness of a fox"],
  "species.oni": ["a powerful frame, concealed horns, and a presence that makes ordinary rooms feel smaller", "a human disguise stretched over unmistakable oni strength"],
  "species.yamabushi_tengu": ["the bearing of a mountain ascetic, a long nose, and wings kept hidden until movement demands them", "weathered pilgrim clothing adapted for rooftops and modern streets"],
  "species.bakeneko": ["feline eyes, an effortless balance, and small gestures that reveal an old cat beneath the human shape", "a stylish human guise with the territorial poise of a well-fed cat"],
  "species.inugami_jiyuu": ["sharp canine senses, a guarded posture, and the quiet intensity of a loyal spirit", "a practical human form that seems ready to track a scent at any moment"],
  "species.jorogumo": ["an alluring human face masking the patient geometry of a spider", "silken details, immaculate clothing, and movements that seem too precise"],
  "species.karakasa_kozo": ["the bright colors and eccentric silhouette of a living umbrella yōkai", "one-eyed, rain-soaked mischief hidden inside an object people dismiss"],
  "species.medama": ["an enormous living eye adapted to a portable, unsettling modern guise", "an unblinking gaze that makes cameras and mirrors feel watched in return"],
  "species.mizuchi": ["serpentine grace, water-dark scales, and a draconic presence kept beneath a human outline", "cool colors and fluid movement suggesting deep water under calm skin"],
  "species.noppera_bo": ["an ordinary human silhouette whose face can become perfectly blank", "carefully chosen clothing that gives witnesses something to remember besides the missing features"],
  "species.raiju": ["storm-bright eyes, restless motion, and hair that lifts before electricity gathers", "a compact beastlike form outlined by sparks and the smell of ozone"],
  "species.rokurokubi": ["an apparently human body with a neck that can cross impossible distances", "scarves and high collars chosen to disguise a supernatural anatomy"],
  "species.tanuki": ["a friendly, disarming human guise with signs of a round-bellied trickster beneath it", "cheap accessories, an easy grin, and a disguise that becomes less convincing when excitement rises"],
  "species.yuki_onna": ["pale winter beauty, breath like frost, and clothing untouched by the cold", "a silent human form whose presence lowers the temperature of the room"],
  "species.hanyo": ["a mostly human appearance marked by one unmistakable inheritance from a yōkai parent", "features poised between human familiarity and a supernatural lineage that refuses to disappear"],
};

const TERRITORY: Record<string, string> = {
  nightlife: "the bars, arcades, stations, and back streets that come alive after midnight",
  technology: "offices, devices, surveillance systems, and networks humans assume they control",
  shrines: "shrines, old records, and rituals preserved inside a rapidly changing neighborhood",
  entertainment: "clubs, stages, studios, and the hungry attention of an audience",
  wild_places: "rivers, forests, coastlines, and the shrinking borders of wild territory",
  hard_neighborhoods: "training halls, guarded properties, and neighborhoods where reputation travels quickly",
};

const WITNESS: Record<string, string> = {
  legend: "left a witness with a story too vivid to remain private",
  false_memory: "survived by replacing the truth with a more convenient memory",
  vanish: "disappeared before the witness could understand what had been seen",
  bargain: "turned a dangerous witness into an uneasy ally through a bargain",
  observe: "watched the witness long enough to learn what fear would make them do",
  overwhelm: "buried the truth beneath an impossible supernatural spectacle",
};

const FEAR: Record<string, string> = {
  violence: "sudden violence",
  trapped: "the certainty of being trapped",
  watched: "the feeling of being watched by something patient",
  survives: "seeing something survive what should have destroyed it",
  reverence: "awe and the need to leave an offering",
  spectacle: "confusion, spectacle, and a story nobody believes",
};

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0);
}

function pick<T>(items: T[], seed: string, offset: number): T {
  if (items.length === 0) throw new Error("Generator pool is empty.");
  return items[(hashString(seed) + offset) % items.length]!;
}

function title(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizeGeneration(value: unknown): GenerationState {
  const empty = emptyGenerationState();
  if (!value || typeof value !== "object" || Array.isArray(value)) return empty;
  const source = value as Partial<GenerationState>;
  return {
    fingerprint: typeof source.fingerprint === "string" ? source.fingerprint : "",
    counters: Object.fromEntries(FIELD_KEYS.map((key) => [key, typeof source.counters?.[key] === "number" ? source.counters[key] : 0])) as Record<GeneratedFieldKey, number>,
    manual: Object.fromEntries(FIELD_KEYS.map((key) => [key, source.manual?.[key] === true])) as Record<GeneratedFieldKey, boolean>,
  };
}

function answer(context: GeneratorContext, questionId: string): string {
  return context.answers[questionId] ?? "";
}

function fingerprint(context: GeneratorContext): string {
  return JSON.stringify({
    species: context.options.species.id,
    answers: Object.entries(context.answers).sort(([a], [b]) => a.localeCompare(b)),
    origin: context.originId,
    profile: context.preview.profile,
    powers: context.preview.selectedPowerIds,
  });
}

function nameSuggestion(context: GeneratorContext, counter: number): NameEntry {
  const pool = [...(NAMES_BY_SPECIES[context.options.species.id] ?? []), ...GENERAL_NAMES];
  return pick(pool, `${fingerprint(context)}|name`, counter);
}

function clanSuggestion(context: GeneratorContext, counter: number): { name: string; description: string } {
  const clan = pick(CLANS, `${fingerprint(context)}|clan`, counter);
  const territory = TERRITORY[answer(context, "q.modern_territory")] ?? "the uneasy boundary between old folklore and modern Japan";
  return {
    name: clan.name,
    description: `${clan.name} takes its symbol from ${clan.image}. ${clan.custom} For this character, its influence is strongest around ${territory}. This is an original, editable Clan suggestion rather than official setting canon.`,
  };
}

function conceptSuggestion(context: GeneratorContext, name: string, counter: number): string {
  const species = context.options.species.label;
  const role = context.preview.profile.generalSkillLabel.toLowerCase();
  const attribute = context.preview.profile.attributeLabel.toLowerCase();
  const monstrous = context.preview.profile.monstrousSkillLabel.toLowerCase();
  const power = context.preview.profile.powerThemeLabel.toLowerCase();
  const variants = [
    `${name} is a ${species} ${role} who relies on ${attribute}, expresses a ${monstrous} supernatural nature, and turns ${power} Arcane Powers into practical solutions.`,
    `A ${attribute}-driven ${species} ${role}, ${name} moves through modern Japan with a ${monstrous} presence and a preference for ${power} supernatural effects.`,
    `${name} is built to serve as the Clan’s ${role}: ${attribute} in approach, ${monstrous} in supernatural style, and drawn toward ${power} Arcane Powers.`,
  ];
  return pick(variants, `${fingerprint(context)}|concept`, counter);
}

function appearanceSuggestion(context: GeneratorContext, name: string, counter: number): string {
  const species = context.options.species.label;
  const base = pick(SPECIES_APPEARANCE[context.options.species.id] ?? [`the recognizable supernatural traits of a ${species}`], `${fingerprint(context)}|appearance`, counter);
  const attribute = context.preview.profile.attributeLabel;
  const monstrous = context.preview.profile.monstrousSkillLabel;
  const variants = [
    `${name} presents ${base}. The overall impression is ${attribute.toLowerCase()} rather than ornamental, while moments of stress reveal the character’s ${monstrous.toLowerCase()} supernatural style.`,
    `At first glance, ${name} appears carefully adapted to modern Japan, but closer attention reveals ${base}. Clothing and posture emphasize a ${attribute.toLowerCase()} build, and the air changes whenever the ${monstrous.toLowerCase()} side surfaces.`,
  ];
  return pick(variants, `${fingerprint(context)}|appearance-copy`, counter);
}

function storySuggestion(context: GeneratorContext, name: string, clan: string, counter: number): { background: string; rumor: string; goal: string; bond: string } {
  const species = context.options.species.label;
  const territory = TERRITORY[answer(context, "q.modern_territory")] ?? "a district where the old supernatural world survives inside modern routines";
  const witness = WITNESS[answer(context, "q.human_witness")] ?? "allowed a human to see more than was safe";
  const rememberedFear = FEAR[answer(context, "q.desired_fear")] ?? "an encounter that cannot be explained away";
  const role = context.preview.profile.generalSkillLabel.toLowerCase();
  const origin = context.definition.origins.find((item) => item.id === context.originId)?.label ?? "unknown origin";
  const variants = [
    `For years, ${name} has operated around ${territory}. As a ${species} shaped by ${origin}, the character learned to remain useful to ${clan || "the local yōkai community"} by acting as a ${role}. The arrangement worked until ${name} ${witness}.\n\nNow a human rumor is spreading faster than the Clan can contain it. ${name} must decide whether to erase the evidence, recruit the witness, or turn the growing story into Fear. The encounter people remember is defined by ${rememberedFear}, and every answer risks exposing more of the hidden world.`,
    `${name} built a routine inside ${territory}, balancing the needs of ${clan || "a loose circle of supernatural allies"} against the expectations of human society. The ${species} became known as a dependable ${role}, especially when ordinary solutions stopped working.\n\nThat reputation changed after ${name} ${witness}. Someone has begun connecting unrelated incidents to the same supernatural figure. To protect the Clan and preserve a life in modern Japan, ${name} must control a legend centered on ${rememberedFear} before a rival, investigator, or frightened human controls it first.`,
  ];
  return {
    background: pick(variants, `${fingerprint(context)}|story`, counter),
    rumor: `People nearby whisper that ${name} appears whenever ${rememberedFear} is about to become real.`,
    goal: `Control the growing human legend without betraying ${clan || "the yōkai who depend on this character"}.`,
    bond: `${name} owes a personal debt to someone connected to ${territory}, even though admitting that attachment would create a weakness.`,
  };
}

function notesSuggestion(context: GeneratorContext, counter: number): string {
  const role = context.preview.profile.generalSkillLabel;
  const theme = context.preview.profile.powerThemeLabel;
  const variants = [
    `Suggested table hooks: a contact who knows the local territory, a small object linked to the character’s origin, one unfinished Clan obligation, and a human witness who remembers too much. The build favors the ${role} role and ${theme} Arcane Powers.`,
    `Possessions and contacts may include a modern tool suited to the ${role} role, a token from the character’s origin, a Clan message that has not been answered, and evidence left with a human witness. The supernatural theme is ${theme}.`,
  ];
  return pick(variants, `${fingerprint(context)}|notes`, counter);
}

export function ensureGeneratedCharacter<T extends GeneratedCharacterPayload>(payload: T, context: GeneratorContext, forceFingerprintRefresh = false): T {
  const generation = normalizeGeneration(payload.generation);
  const nextFingerprint = fingerprint(context);
  const changed = generation.fingerprint !== nextFingerprint;
  generation.fingerprint = nextFingerprint;

  const next = { ...payload, generation } as T;
  const refresh = changed || forceFingerprintRefresh;
  const name = nameSuggestion(context, generation.counters.name);
  if (!generation.manual.name && (refresh || !next.characterName.trim())) {
    next.characterName = name.romanized;
    next.nameKanji = name.kanji;
    next.nameMeaning = name.meaning;
  }

  const clan = clanSuggestion(context, generation.counters.clan);
  if (!generation.manual.clan && (refresh || !next.clan.trim())) {
    next.clan = clan.name;
    next.clanDescription = clan.description;
  }

  if (!generation.manual.concept && (refresh || !next.characterConcept.trim())) {
    next.characterConcept = conceptSuggestion(context, next.characterName || name.romanized, generation.counters.concept);
  }

  if (!generation.manual.appearance && (refresh || !next.appearance.trim())) {
    next.appearance = appearanceSuggestion(context, next.characterName || name.romanized, generation.counters.appearance);
  }

  if (!generation.manual.story && (refresh || !next.background.trim())) {
    const story = storySuggestion(context, next.characterName || name.romanized, next.clan || clan.name, generation.counters.story);
    next.background = story.background;
    next.rumor = story.rumor;
    next.goal = story.goal;
    next.bond = story.bond;
  }

  if (!generation.manual.notes && (refresh || !next.notes.trim())) {
    next.notes = notesSuggestion(context, generation.counters.notes);
  }
  return next;
}

export function markGeneratedFieldManual<T extends GeneratedCharacterPayload>(payload: T, field: GeneratedFieldKey): T {
  const generation = normalizeGeneration(payload.generation);
  generation.manual[field] = true;
  return { ...payload, generation } as T;
}

export function rerollGeneratedField<T extends GeneratedCharacterPayload>(payload: T, context: GeneratorContext, field: GeneratedFieldKey): T {
  const generation = normalizeGeneration(payload.generation);
  generation.counters[field] += 1;
  generation.manual[field] = false;
  let next = { ...payload, generation } as T;

  if (field === "name") {
    const name = nameSuggestion(context, generation.counters.name);
    next.characterName = name.romanized;
    next.nameKanji = name.kanji;
    next.nameMeaning = name.meaning;
    if (!generation.manual.concept) next.characterConcept = conceptSuggestion(context, next.characterName, generation.counters.concept);
    if (!generation.manual.appearance) next.appearance = appearanceSuggestion(context, next.characterName, generation.counters.appearance);
    if (!generation.manual.story) {
      const story = storySuggestion(context, next.characterName, next.clan, generation.counters.story);
      next.background = story.background; next.rumor = story.rumor; next.goal = story.goal; next.bond = story.bond;
    }
    return next;
  }

  if (field === "clan") {
    const clan = clanSuggestion(context, generation.counters.clan);
    next.clan = clan.name; next.clanDescription = clan.description;
    if (!generation.manual.story) {
      const story = storySuggestion(context, next.characterName, next.clan, generation.counters.story);
      next.background = story.background; next.rumor = story.rumor; next.goal = story.goal; next.bond = story.bond;
    }
    return next;
  }

  if (field === "concept") next.characterConcept = conceptSuggestion(context, next.characterName, generation.counters.concept);
  if (field === "appearance") next.appearance = appearanceSuggestion(context, next.characterName, generation.counters.appearance);
  if (field === "story") {
    const story = storySuggestion(context, next.characterName, next.clan, generation.counters.story);
    next.background = story.background; next.rumor = story.rumor; next.goal = story.goal; next.bond = story.bond;
  }
  if (field === "notes") next.notes = notesSuggestion(context, generation.counters.notes);
  return next;
}

export function generatedFieldWasCustomized(payload: GeneratedCharacterPayload, field: GeneratedFieldKey): boolean {
  return normalizeGeneration(payload.generation).manual[field];
}

export function generationContext(options: BuildOptions, definition: GuidedBuildDefinition, preview: GuidedBuildPreview, payload: GeneratedCharacterPayload): GeneratorContext {
  return { options, definition, preview, answers: payload.guidedAnswers, originId: payload.originId };
}

export function fieldLabel(field: GeneratedFieldKey): string {
  return title(field === "story" ? "backstory" : field);
}
